export const NOT_ENOUGH_MESSAGE = 'Amount is greater than available amount';
export const NOTHING_LEFT_MESSAGE = 'Enter amount first / nothing left to allocate';

export const num = (value) => {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : 0;
};

export const round3 = (value) => Math.round((num(value) + Number.EPSILON) * 1000) / 1000;

export const fmt3 = (value) => round3(value).toFixed(3);

// Amount to put on a row when it is ticked: never negative, never above what is left or the row balance.
export const autoAllocate = (unallocated, rowBalance) => {
    const value = Math.min(num(unallocated), num(rowBalance));
    return value > 0 ? round3(value) : 0;
};

// rows: [{ amount }]. Allocated includes the additional (FX) amount, which can be negative.
export const summary = (amount, rows, additional) => {
    const gridTotal = (rows || []).reduce((sum, row) => sum + num(row?.amount), 0);
    const allocated = round3(gridTotal + num(additional));
    return { allocated, unallocated: round3(num(amount) - allocated) };
};

// Ticks or unticks a row. Returns { rows, error }; on error rows is unchanged.
export const toggleRow = (rows, index, amount, additional) => {
    const row = rows[index];
    if (!row) return { rows, error: null };
    const next = rows.slice();
    if (row.selected) {
        next[index] = { ...row, selected: false, amount: 0 };
        return { rows: next, error: null };
    }
    const { unallocated } = summary(amount, rows, additional);
    if (unallocated <= 0) return { rows, error: NOTHING_LEFT_MESSAGE };
    const value = autoAllocate(unallocated, row.balance);
    if (value <= 0) return { rows, error: NOTHING_LEFT_MESSAGE };
    next[index] = { ...row, selected: true, amount: value };
    return { rows: next, error: null };
};

// Applies a typed amount to a ticked row. Returns { rows, error }; on error rows is unchanged (revert).
export const setRowAmount = (rows, index, rawValue, amount, additional) => {
    const row = rows[index];
    if (!row || !row.selected) return { rows, error: null };
    const value = round3(rawValue);
    const next = rows.slice();
    if (value === 0) {
        next[index] = { ...row, selected: false, amount: 0 };
        return { rows: next, error: null };
    }
    if (value < 0 || value > round3(row.balance)) return { rows, error: NOT_ENOUGH_MESSAGE };
    next[index] = { ...row, amount: value };
    const { unallocated } = summary(amount, next, additional);
    if (unallocated < 0) return { rows, error: NOT_ENOUGH_MESSAGE };
    return { rows: next, error: null };
};

export const clearAllocations = (rows) => (rows || []).map((row) => ({ ...row, selected: false, amount: 0 }));

// After a re-fetch of invoices keep ticks (and amounts) that are still valid against the new balance.
export const mergeKeepingTicks = (oldRows, freshInvoices) => {
    const previous = new Map((oldRows || []).map((row) => [row.receiptNo, row]));
    return (freshInvoices || []).map((invoice) => {
        const old = previous.get(invoice.receiptNo);
        if (old?.selected && num(old.amount) > 0 && num(old.amount) <= round3(invoice.balance)) {
            return { ...invoice, selected: true, amount: old.amount };
        }
        return { ...invoice, selected: false, amount: 0 };
    });
};
